import React, {useEffect, useRef} from 'react'
import {useSelector} from 'react-redux'

import {useDeviceLayout} from '@vio/atlas/context/deviceLayout'
import {Box} from '@vio/atlas/helpers/Box'
import {SmoothScrollNavigation} from '@vio/atlas/helpers/SmoothScrollNavigation'
import {SkeletonLoader} from '@vio/atlas/SkeletonLoader'
import {useDispatchTeamEvent} from '@vio/core/src/analytics/hooks/useDispatchTeamEvent'
import {trackEvent} from '@vio/core/src/analytics/modules/actions'
import {Action, Category, Entity} from '@vio/core/src/analytics/types/Events'
import {useIsHydrated} from '@vio/core/src/utils/react/hooks/useIsHydrated'

import {getIsRtlLanguage} from '../../../modules/meta/selectors'
import {HighlightedTitle} from '../../Home/HighlightedTitle'
import {useHopperAd} from '../../HopperAds/hooks/useHopperAd'
import {useHopperAdEligibility} from '../../HopperAds/hooks/useHopperAdEligibility'
import {HopperCarouselItem} from '../../HopperAds/HopperCarouselItem'
import {HopperCarouselItemSkeleton} from '../../HopperAds/HopperCarouselItemSkeleton'
import {Title} from '../components/Title'

export const HopperAds = React.memo(() => {
  const hasTrackedLoaded = useRef(false)
  const isHydrated = useIsHydrated()
  const isRtlLanguage = useSelector(getIsRtlLanguage)
  const {isMobile} = useDeviceLayout()
  const dispatchTeamEvent = useDispatchTeamEvent()

  const {isEligible} = useHopperAdEligibility('Carousel')
  const {isLoading, ads, error} = useHopperAd({
    variant: 'Carousel',
    enabled: isHydrated && isEligible
  })
  const isErrored = !!error && ads.length === 0

  useEffect(() => {
    if (
      !hasTrackedLoaded.current &&
      isHydrated &&
      isEligible &&
      !isLoading &&
      ads.length > 0 &&
      !isErrored
    ) {
      dispatchTeamEvent(
        trackEvent({
          category: Category.System,
          entity: Entity.HopperCarousel,
          action: Action.Loaded
        })
      )
      hasTrackedLoaded.current = true
    }
  }, [
    ads.length,
    isEligible,
    isErrored,
    isHydrated,
    isLoading,
    dispatchTeamEvent
  ])

  if (
    !isHydrated ||
    !isEligible ||
    (!isLoading && ads.length === 0) ||
    isErrored
  ) {
    return null
  }

  if (isLoading && ads.length === 0) {
    return (
      <div>
        <Box paddingLeft={isMobile ? 's500' : 's000'} marginBottom="s300">
          <SkeletonLoader width={200} />
        </Box>
        <SmoothScrollNavigation
          isRtlLanguage={isRtlLanguage}
          hasSidePaddings={isMobile}
          itemsGap="s300"
        >
          {Array.from({length: 7}, (_, i) => (
            <HopperCarouselItemSkeleton key={i} />
          ))}
        </SmoothScrollNavigation>
      </div>
    )
  }

  return (
    <div>
      <Title variant="titleXL">
        <HighlightedTitle source="HopperAds" />
      </Title>
      <SmoothScrollNavigation
        isRtlLanguage={isRtlLanguage}
        hasSidePaddings={isMobile}
        itemsGap="s300"
      >
        {ads.map(ad => (
          <HopperCarouselItem key={ad.adId} ad={ad} />
        ))}
      </SmoothScrollNavigation>
    </div>
  )
})
HopperAds.displayName = 'HopperAds'
